'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';

interface NavigationProps {
  currentSection?: string;
}

/**
 * Site header.
 *
 * About / What I Do / Impact, a hairline rule, then AI Adoption and the one
 * ink-blue button. Anchor order follows document order because the active
 * highlight is driven by the homepage scroll spy.
 */

interface NavItem {
  id: string;
  label: string;
  href: string;
  /** True when the href targets a homepage anchor rather than its own route. */
  isSection: boolean;
}

// Anchors first, in document order, then page routes. The separator is drawn
// between the two groups.
const SECTION_ITEMS: NavItem[] = [
  { id: 'founder', label: 'About', href: '/#founder', isSection: true },
  { id: 'services', label: 'What I Do', href: '/#services', isSection: true },
  { id: 'impact', label: 'Impact', href: '/#impact', isSection: true },
];

const PAGE_ITEMS: NavItem[] = [
  { id: 'ai-adoption', label: 'AI Adoption', href: '/ai-adoption', isSection: false },
];

export default function Navigation({ currentSection }: NavigationProps) {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const isHomePage = pathname === '/';
  const isContactPage = pathname === '/contact';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    if (id === 'hero' && window.scrollY === 0) {
      setIsMenuOpen(false);
      return;
    }

    const element = document.getElementById(id);
    if (element) {
      const targetPosition = element.offsetTop - 80;
      window.scrollTo({ top: targetPosition, behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const isActiveItem = (item: NavItem) => {
    if (!item.isSection) return pathname === item.href;
    return isHomePage && currentSection === item.id;
  };

  // Hrefs are absolute ("/#services"), so from /contact or /ai-adoption the
  // Link navigates home and the browser resolves the fragment. Only on the
  // homepage itself do we intercept and smooth-scroll.
  const handleNavClick = (item: NavItem, e: React.MouseEvent) => {
    if (item.isSection && isHomePage) {
      e.preventDefault();
      scrollToSection(item.id);
    }
    setIsMenuOpen(false);
  };

  const itemClasses = (isActive: boolean) =>
    `font-sans text-[15px] transition-colors duration-150 ${
      isActive ? 'text-ink-blue font-semibold' : 'text-body hover:text-ink'
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 bg-sand border-b border-line transition-all duration-200 ${
        isScrolled ? 'py-3 shadow-sm' : 'py-4 md:py-[18px]'
      }`}
    >
      <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-[72px]">
        <div className="flex items-center justify-between">
          {/* No aria-label: the wordmark inside Logo is visible text, so the
              link already has an accessible name. */}
          <Link href="/" className="logo-interactive">
            <Logo size={24} showText={true} />
          </Link>

          <div className="hidden lg:flex items-center gap-[30px]">
            {SECTION_ITEMS.map((item) => (
              <Link
                key={item.id}
                href={item.href}
                onClick={(e) => handleNavClick(item, e)}
                className={itemClasses(isActiveItem(item))}
              >
                {item.label}
              </Link>
            ))}

            <span aria-hidden="true" className="h-5 w-px bg-line" />

            {PAGE_ITEMS.map((item) => (
              <Link
                key={item.id}
                href={item.href}
                onClick={(e) => handleNavClick(item, e)}
                className={itemClasses(isActiveItem(item))}
              >
                {item.label}
              </Link>
            ))}

            <Link
              href="/contact"
              // .cta-lift carries the background-color transition as well as
              // the hover lift, so transition-colors is not repeated here: it
              // is a layered utility and the unlayered .cta-lift rule would
              // replace its transition-property outright.
              className={`cta-lift inline-flex items-center whitespace-nowrap rounded-md px-5 py-2.5 font-sans text-[15px] font-semibold ${
                isContactPage
                  ? 'bg-sand-accent text-sand-accent-text'
                  : 'bg-ink-blue text-on-blue hover:bg-ink-blue/90'
              }`}
            >
              Start a Conversation
            </Link>
          </div>

          <button
            className="lg:hidden -mr-2 p-2 text-ink transition-colors hover:text-ink-blue"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isMenuOpen ? 'max-h-[420px] opacity-100 mt-4 pb-2' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="flex flex-col gap-1 border-t border-line pt-3">
            {[...SECTION_ITEMS, ...PAGE_ITEMS].map((item) => (
              <Link
                key={item.id}
                href={item.href}
                onClick={(e) => handleNavClick(item, e)}
                className={`flex min-h-11 items-center rounded-md px-3 font-sans text-base transition-colors duration-150 ${
                  isActiveItem(item)
                    ? 'text-ink-blue font-semibold bg-sand-2'
                    : 'text-body hover:bg-sand-2'
                }`}
              >
                {item.label}
              </Link>
            ))}

            <Link
              href="/contact"
              onClick={() => setIsMenuOpen(false)}
              className={`mt-2 flex min-h-11 items-center justify-center rounded-md px-5 font-sans text-base font-semibold ${
                isContactPage
                  ? 'bg-sand-accent text-sand-accent-text'
                  : 'bg-ink-blue text-on-blue'
              }`}
            >
              Start a Conversation
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
